import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import { useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import api from '../api';

export default function UploadStatementDialog({ open, onClose, onUploaded = () => {} }) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const onCloseInternal = useCallback(
    (event, reason) => {
      if (reason === 'backdropClick') {
        return;
      }

      setSelectedFile(null);
      onClose();
    },
    [onClose]
  );

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length) {
      setSelectedFile(acceptedFiles[0]);
    }
  }, []);

  const onUpload = useCallback(() => {
    if (!selectedFile) {
      return;
    }

    const formData = new FormData();
    formData.append('statement', selectedFile);
    setIsUploading(true);

    api
      .post('/cardStatementTransactions/upload-statement', formData)
      .then(() => {
        setIsUploading(false);
        onUploaded();
        onCloseInternal();
        toast.success(t('Statement processed successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
      })
      .catch((err) => {
        setIsUploading(false);
        console.error(err);
        toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
      });
  }, [selectedFile, onUploaded, onCloseInternal, t]);

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    autoFocus: true,
    onDrop,
    onError: (err) => console.error(err),
    maxFiles: 1,
    maxSize: 10000000,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
    },
  });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const dropAreaStyle = useMemo(() => {
    const border = isDragAccept
      ? { borderColor: theme.palette.success.main }
      : isDragReject
      ? { borderColor: theme.palette.error.main }
      : { borderColor: '#8a8a8a' };

    const common = {
      padding: '8px',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: '2px',
      borderRadius: '3%',
      borderStyle: 'dashed',
    };

    return fullScreen
      ? {
          width: '100%',
          height: '100%',
          ...common,
          ...border,
        }
      : {
          minWidth: 400,
          minHeight: 300,
          ...common,
          ...border,
        };
  }, [theme, isDragAccept, isDragReject, fullScreen]);

  return (
    <Dialog fullScreen={fullScreen} open={open} onClose={onCloseInternal}>
      <DialogTitle gutterBottom>{t('Upload Bank Statements')}</DialogTitle>
      <Divider />
      <DialogContent>
        <div {...getRootProps()} style={dropAreaStyle}>
          <input {...getInputProps()} />
          {!selectedFile &&
            (isDragActive ? (
              <Typography variant="body2">{t('Drop statement here...')}</Typography>
            ) : (
              <Typography variant="body2">{t("Drag 'n' drop statement here, or click to select it")}</Typography>
            ))}
          {selectedFile && selectedFile.name}
        </div>
        <Box pt={2} pb={2}>
          <Typography variant="body2">{t('Max size: 10MB')}</Typography>
          <Typography variant="body2">{t('File format: CSV')}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCloseInternal}>
          {t('Cancel')}
        </Button>
        <Button onClick={onUpload} disabled={!selectedFile || isUploading} variant="contained">
          {isUploading && <CircularProgress size={24} sx={{ ml: 1, color: 'white' }} />}
          {t('Upload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
